import React, { useState, FC, useEffect } from 'react';
import {
  FlexRow,
  Box,
  useDirection,
  keyframes,
  Glyph,
} from '@edreamsodigeo/prisma-design-system';
import { useFavorites } from 'clientState/providers/FavoritesListProvider/FavoritesListProvider';
import useFavoriteResultsTracking from 'clientState/hooks/useFavoriteResultsTracking/useFavoriteResultsTracking';

type FavoriteProps = {
  itineraryId: string | undefined;
  withRetailPills?: boolean;
};

const beatAnimation = keyframes`
0% {
  transform: scale(1);
}
50% {
  transform: scale(1.25);
}
100%{
  transform: scale(1);
}
`;
export const Favorite: FC<FavoriteProps> = ({
  itineraryId,
  withRetailPills = true,
}) => {
  const trackFavoriteResultsTracking = useFavoriteResultsTracking();
  const [runAnimation, setRunAnimation] = useState(false);
  const { favorites, addFavorite, removeFavorite } = useFavorites();
  const isSelected = !!favorites.find(
    (favoriteId) => favoriteId?.id === itineraryId
  );
  const isRtl = useDirection();
  let timeout;

  useEffect(() => {
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [timeout]);

  const onClick = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (isSelected) {
      removeFavorite(itineraryId);
    } else {
      addFavorite(itineraryId);
    }
    trackFavoriteResultsTracking();
    setRunAnimation(true);
    timeout = setTimeout(() => {
      setRunAnimation(false);
    }, 500);
  };

  return (
    <Box
      mr={isRtl ? '0' : 'minus_base'}
      ml={isRtl ? 'minus_base' : '0'}
      mt="minus_base"
      sx={{
        cursor: 'pointer',
      }}
    >
      <FlexRow
        onClick={onClick}
        height={withRetailPills ? '56px' : '24px'}
        mt={withRetailPills ? 'unset' : 'base'}
        width="56px"
        justifyContent="center"
        alignItems="center"
        data-testid="favorite"
      >
        <Glyph
          data-testid="favorite-icon"
          category="UIActions"
          name={isSelected ? 'Favorite' : 'FavoriteOutline'}
          sx={{
            '&:hover': {
              color: isSelected ? 'red.30' : 'neutral.70',
            },
            '&:active': {
              transform: 'scale(0.75)',
            },
            color: isSelected ? 'red.50' : 'neutral.80',
            animation: runAnimation
              ? `${beatAnimation} 0.5s ease-in-out`
              : null,
          }}
        />
      </FlexRow>
    </Box>
  );
};

export default Favorite;
